import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Bars3Icon, XMarkIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';
import SearchButton from '../search/SearchButton';
// Using SVG logo from public directory

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isResourcesOpen, setIsResourcesOpen] = useState(false);
  const dropdownRef = useRef(null);
  const location = useLocation();
  const { isAuthenticated, user, logout } = useAuth();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsResourcesOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const navigation = [
    { name: 'Home', href: '/' },
    { name: 'Sports', href: '/sports' },
    { name: 'News', href: '/news' },
    { name: 'Events', href: '/events' },
    { name: 'Media', href: '/media' },
    { name: 'Downloads', href: '/downloads' },
    { name: 'Resources', href: '/family-resources' },
    { name: 'Contact', href: '/contact' }
  ];

  const resourcesDropdown = [
    { name: 'Family Resources', href: '/family-resources' },
    { name: 'Facilities', href: '/facilities' },
    { name: 'Safety', href: '/safety' },
    { name: 'Coach Resources', href: '/coach-resources' }
  ];

  const isActivePath = (path) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  return (
    <header className="bg-accent-50 shadow-sm border-b border-primary-200 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo and School Name */}
          <div className="flex items-center">
            <Link to="/" className="flex items-center space-x-3">
              {/* East End Prep Logo */}
              <img 
                src="/east-end-prep-logo.png"
                alt="East End Prep Eagles Logo" 
                className="w-24 h-24"
              />
              <div>
                <h1 className="text-xl font-bold text-primary-900">East End Prep Sports</h1>
                <p className="text-xs text-secondary-500 font-medium">Eagles Believe, Eagles Achieve</p>
              </div>
            </Link>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex space-x-6">
            {navigation.map((item) => {
              if (item.name === 'Resources') {
                return (
                  <div key={item.name} className="relative" ref={dropdownRef}>
                    <button
                      onClick={() => setIsResourcesOpen(!isResourcesOpen)}
                      className={`nav-link flex items-center space-x-1 ${
                        resourcesDropdown.some(subItem => isActivePath(subItem.href)) ? 'active' : ''
                      }`}
                    >
                      <span>{item.name}</span>
                      <ChevronDownIcon className={`h-4 w-4 transition-transform duration-200 ${
                        isResourcesOpen ? 'rotate-180' : ''
                      }`} />
                    </button>
                    
                    {/* Resources Dropdown */}
                    {isResourcesOpen && (
                      <div className="absolute top-full left-0 mt-1 w-48 bg-accent-50 rounded-md shadow-lg border border-primary-200 py-1 z-50">
                        {resourcesDropdown.map((subItem) => (
                          <Link
                            key={subItem.name}
                            to={subItem.href}
                            className={`block px-4 py-2 text-sm transition-colors duration-200 ${
                              isActivePath(subItem.href)
                                ? 'text-primary-900 bg-primary-100'
                                : 'text-primary-700 hover:text-primary-900 hover:bg-primary-50'
                            }`}
                            onClick={() => setIsResourcesOpen(false)}
                          >
                            {subItem.name}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                );
              }
              
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`nav-link ${
                    isActivePath(item.href) ? 'active' : ''
                  }`}
                >
                  {item.name}
                </Link>
              );
            })}
          </nav>

          {/* Search and User Menu */}
          <div className="hidden md:flex items-center space-x-4">
            <SearchButton />
            {isAuthenticated ? (
              <div className="flex items-center space-x-4">
                <span className="text-sm text-primary-700">
                  Welcome, {user?.firstName}
                </span>
                {user?.role && ['admin', 'system_admin'].includes(user.role) && (
                  <Link
                    to="/admin"
                    className="btn-primary text-sm py-1 px-3"
                  >
                    Admin
                  </Link>
                )}
                <button
                  onClick={handleLogout}
                  className="text-sm text-primary-500 hover:text-primary-700"
                >
                  Logout
                </button>
              </div>
            ) : (
              <Link
                to="/login"
                className="btn-primary text-sm py-2 px-4"
              >
                Login
              </Link>
            )}
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="text-primary-500 hover:text-primary-700 focus:outline-none focus:text-primary-700"
            >
              {isMenuOpen ? (
                <XMarkIcon className="h-6 w-6" />
              ) : (
                <Bars3Icon className="h-6 w-6" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation Menu */}
      {isMenuOpen && (
        <div className="md:hidden bg-accent-50 border-t border-primary-200">
          <div className="px-2 pt-2 pb-3 space-y-1">
            {/* Mobile Search */}
            <div className="px-3 py-2">
              <SearchButton className="w-full justify-center" />
            </div>
            {navigation.map((item) => {
              if (item.name === 'Resources') {
                return resourcesDropdown.map((subItem) => (
                  <Link
                    key={subItem.name}
                    to={subItem.href}
                    className={`block px-3 py-2 text-base font-medium rounded-md transition-colors duration-200 ${
                      isActivePath(subItem.href)
                        ? 'text-primary-900 bg-primary-100'
                        : 'text-primary-700 hover:text-primary-900 hover:bg-primary-50'
                    }`}
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {subItem.name}
                  </Link>
                ));
              }
              
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`block px-3 py-2 text-base font-medium rounded-md transition-colors duration-200 ${
                    isActivePath(item.href)
                      ? 'text-primary-900 bg-primary-100'
                      : 'text-primary-700 hover:text-primary-900 hover:bg-primary-50'
                  }`}
                  onClick={() => setIsMenuOpen(false)}
                >
                  {item.name}
                </Link>
              );
            })}
            
            {/* Mobile User Menu */}
            <div className="border-t border-primary-200 pt-3 mt-3">
              {isAuthenticated ? (
                <div className="space-y-2">
                  <div className="px-3 py-2">
                    <p className="text-sm font-medium text-primary-900">
                      {user?.firstName} {user?.lastName}
                    </p>
                    <p className="text-xs text-primary-500 capitalize">
                      {user?.role}
                    </p>
                  </div>
                  
                  {user?.role && ['admin', 'system_admin'].includes(user.role) && (
                    <Link
                      to="/admin"
                      className="block px-3 py-2 text-base font-medium text-primary-900 hover:bg-primary-100 rounded-md"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      Admin Dashboard
                    </Link>
                  )}
                  
                  <button
                    onClick={() => {
                      handleLogout();
                      setIsMenuOpen(false);
                    }}
                    className="block w-full text-left px-3 py-2 text-base font-medium text-primary-700 hover:text-primary-900 hover:bg-primary-50 rounded-md"
                  >
                    Logout
                  </button>
                </div>
              ) : (
                <Link
                  to="/login"
                  className="block px-3 py-2 text-base font-medium text-primary-900 hover:bg-primary-100 rounded-md"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Login
                </Link>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;